// The shapes BidCard's props take, as validators.
//
// `type: Object` says only that an object arrived. What it cannot say is WHICH
// MEMBERS, and that is the gap that fails silently: a card handed
// `{ label: 'WON' }` for a headline whose member is `text` renders an empty
// badge — no warning, nothing in the console, and the only symptom is one blank
// slot in a lane of sixty cards.
//
// So these are CLOSED. An unknown key is rejected rather than ignored, on the
// same argument that closes the `i18n` validator next door: a member the card
// never reads is a member the caller believes it does. A typo, a stale member
// name after a rename, and a shape borrowed from the wrong prop all land here
// instead of on the screen.
//
// They live beside the component rather than inside it because `defineProps`
// cannot reference a local declaration in `<script setup>` — the options object
// is hoisted out of setup, so a validator must be imported or written inline.
// Two closed shapes are more than an inline arrow should carry.

// Both predicates live in `-shared` now, because AvatarChip's `imageUrl` takes
// the same shape BidCard's does and `-shared` cannot import from here. They are
// re-exported rather than re-homed so every existing import keeps working —
// the same arrangement utils/formatters.js uses for the avatar gradient.
export {
  isNonBlankString,
  isAbsentOrNonBlankString,
} from '@hds/auto-auction-com-components-shared/utils/propValidators.js';

// Imported separately because the closed shapes below call it; the re-export
// above is for the components that import it from here.
import { isNonBlankString } from '@hds/auto-auction-com-components-shared/utils/propValidators.js';

/** Absent, or passing. `undefined` is how an optional member says it is not there. */
const isAbsentOr = (value, predicate) => value === undefined || predicate(value);

/**
 * A plain object carrying no key outside `allowed`.
 *
 * Rejects arrays and `null` as well, both of which are `typeof 'object'` and
 * neither of which is a member bag — an array reaching a prop that wanted
 * `{ text }` is a real mistake and reads as an empty slot otherwise.
 */
function isClosedObject(value, allowed) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.keys(value).every((key) => allowed.includes(key));
}

/**
 * `{ text, icon }` — the header's right edge.
 *
 * `text` is required: the slot is the words, and a headline that is only a glyph
 * is a colored mark with nothing saying what it means.
 */
export function isValidHeadline(headline) {
  if (headline === null) return true;
  if (!isClosedObject(headline, ['text', 'icon'])) return false;
  return isNonBlankString(headline.text) && isAbsentOr(headline.icon, isNonBlankString);
}

/**
 * `{ text, icon }` — the whole action row.
 *
 * Both members are optional, because the object's PRESENCE is the signal and
 * `{}` is a complete card: the words fall back to "Bidding unavailable" and the
 * glyph to a padlock.
 */
export function isValidDisabled(disabled) {
  if (disabled === null) return true;
  if (!isClosedObject(disabled, ['text', 'icon'])) return false;
  return isAbsentOr(disabled.text, isNonBlankString) && isAbsentOr(disabled.icon, isNonBlankString);
}
